import {
  lock_hooks,
  useApplicationDetail,
  useCustomer,
  useInsurance,
  useLoanApplication,
  usePerson,
  useUpdateCustomer,
  useUpdateInsurance,
} from '@axo/shared/data-access/hooks';
import { AceCardSection } from '../AceCard';
import { Button, InputGroup, Tooltip } from '@blueprintjs/core';
import { ReactNode, useEffect, useState } from 'react';
import { FI as payoutTableFinland } from './payoutAndPremium';
import styles from './InsuranceCardSection.module.css';
import { AppToaster } from '../AppToaster';
import { InsuranceStatusTag } from './InsuranceStatusTag';
import { TerminationWizard } from './TerminationWizard';
import { formatDatetime } from '../../utils/formatDate';
import { CustomerEmailInput } from './CustomerEmailInput';
import { PayoutAndPremiumDialog } from './PayoutAndPremiumDialog';
import { InsuranceLevel } from './InsuranceLevel.tsx';
import { useInsurancePersonIsEligible as useInsurancePersonIsEligibleV2 } from '@axo/shared/data-access/hooks/queries/useInsurancePersonIsEligible.v2';
import { InsuranceEligibilityStatus } from '@axo/shared/data-access/types/insurance';
import { InsuranceEligibilityStatusTag } from './InsuranceEligibilityStatusTag.tsx';

function Row({ children }: { children: ReactNode }) {
  return <div className={styles.row}>{children}</div>;
}

export function InsuranceCardSection({
  applicationID,
  customerID,
}: {
  applicationID: string;
  customerID: string;
}) {
  const detail = useApplicationDetail(applicationID);

  const insurance = useInsurance(detail.data?.PersonID);

  const { data: applicationData } = useLoanApplication(applicationID);
  const currentPerson = usePerson(applicationData?.PersonID);

  const {
    mutateAsync: postInsuranceEligibilityV2,
    data: eligibilityCheckData,
  } = useInsurancePersonIsEligibleV2();
  const customer = useCustomer(customerID);
  const hasLock = lock_hooks.useCurrentUserHasLock(
    applicationID,
    detail.data?.MarketCountry,
  );

  const updateCustomer = useUpdateCustomer();
  const updateInsurance = useUpdateInsurance();

  const activeInsurance = insurance.data
    ?.sort((a, b) => b.UpdatedAt.localeCompare(a.UpdatedAt))
    .find((policy) => policy.Status === 'active');

  const latest = activeInsurance
    ? activeInsurance
    : insurance.data
        ?.filter((policy) => policy.Status !== 'new')
        .sort((a, b) => b.UpdatedAt.localeCompare(a.UpdatedAt))[0];

  const [updateAmount, setUpdateAmount] = useState<number | null>(null);
  const [updateEmail, setUpdateEmail] = useState<string | null>(null);

  useEffect(() => {
    if (updateCustomer.isSuccess) {
      AppToaster.then((toaster) =>
        toaster.show({
          intent: 'success',
          message: 'E-mail updated',
        }),
      );
    }
  }, [updateCustomer.isSuccess]);

  useEffect(() => {
    if (updateInsurance.isSuccess) {
      AppToaster.then((toaster) =>
        toaster.show({
          intent: 'success',
          message: 'Insurance updated',
        }),
      );
    }
  }, [updateInsurance.isSuccess]);

  useEffect(() => {
    if (
      insurance.data &&
      insurance.isSuccess &&
      !latest &&
      applicationData?.CustomerID
    ) {
      const params = {
        Brand: applicationData?.Meta?.Brand || '',
        PersonID: applicationData?.PersonID || '',
        CustomerID: applicationData?.CustomerID || '',
        ClientID: '',
        LoanApplicationID: applicationData?.ID || '',
        Source: 'ACE',
        FirstName: currentPerson.data?.FirstName || '',
        LastName: currentPerson.data?.LastName || '',
        MarketCountry: applicationData?.MarketCountry || '',
      };
      postInsuranceEligibilityV2(params);
    }
  }, [
    applicationData?.CustomerID,
    applicationData?.ID,
    applicationData?.MarketCountry,
    applicationData?.Meta?.Brand,
    applicationData?.PersonID,
    currentPerson.data?.FirstName,
    currentPerson.data?.LastName,
    insurance.data,
    insurance.isSuccess,
    latest,
    postInsuranceEligibilityV2,
  ]);

  const [terminationWizardOpen, setTerminationWizardOpen] = useState(false);

  return (
    <AceCardSection
      id={`Insurance.${applicationID}`}
      title="Insurance"
      tag={
        <div className={styles.tagContainer}>
          {eligibilityCheckData && (
            <Tooltip
              disabled={
                eligibilityCheckData.Status !==
                InsuranceEligibilityStatus.DENIED
              }
              placement={'top'}
              content={eligibilityCheckData.DenyReason}
              fill
            >
              <InsuranceEligibilityStatusTag
                status={eligibilityCheckData.Status}
              />
            </Tooltip>
          )}
          {latest && (
            <div className={styles.tag}>
              <InsuranceStatusTag status={latest?.Status} />
            </div>
          )}
        </div>
      }
    >
      {latest ? (
        <div className={styles.form}>
          <Row>
            <CustomerEmailInput
              customer={customer}
              insurance={latest}
              setUpdateEmail={setUpdateEmail}
              updateEmail={updateEmail}
              updateCustomer={updateCustomer}
              disabled={!hasLock.data}
            />
          </Row>
          <Row>
            <InputGroup
              leftIcon="dollar"
              readOnly
              value={
                InsuranceLevel({
                  amount: latest.Amount,
                  currency: detail.data?.Currency,
                }) ?? ''
              }
              className={styles.rowInputGroup}
              disabled={!hasLock.data}
              inputClassName={styles.rowInput}
              rightElement={
                latest.Status === 'active' ? (
                  <Button
                    minimal
                    icon="edit"
                    disabled={!hasLock.data}
                    text="Edit"
                    onClick={() =>
                      setUpdateAmount(
                        latest.Amount ?? payoutTableFinland[0].payout,
                      )
                    }
                  />
                ) : undefined
              }
            />
            <PayoutAndPremiumDialog
              updateAmount={updateAmount}
              setUpdateAmount={setUpdateAmount}
              currency={detail.data?.Currency}
              updateInsurance={updateInsurance}
              latest={latest}
              disabled={!hasLock.data}
            />
          </Row>
          {latest.SignedAt && (
            <Row>
              <span className={styles.label}>Signed at</span>
              <div className={styles.actions}>
                {formatDatetime(latest.SignedAt)}
              </div>
            </Row>
          )}
          {latest.TerminatedAt && (
            <Row>
              <span className={styles.label}>Terminated at</span>
              <div className={styles.actions}>
                {formatDatetime(latest.TerminatedAt)}
              </div>
            </Row>
          )}
          {latest.TerminationReceivedAt && (
            <Row>
              <span className={styles.label}>Termination received at</span>
              <div className={styles.actions}>
                {formatDatetime(latest.TerminationReceivedAt)}
              </div>
            </Row>
          )}
          {!latest.TerminatedAt && (
            <Row>
              <span className={styles.label}>Termination</span>
              <div className={styles.actions}>
                <Button
                  minimal
                  intent="danger"
                  text="Terminate insurance"
                  icon="trash"
                  disabled={!hasLock.data || latest.Status !== 'active'}
                  onClick={() => setTerminationWizardOpen(true)}
                />
                <TerminationWizard
                  id={latest.ID}
                  isOpen={terminationWizardOpen}
                  onClose={() => setTerminationWizardOpen(false)}
                />
              </div>
            </Row>
          )}
        </div>
      ) : eligibilityCheckData?.Status ===
        InsuranceEligibilityStatus.APPROVED ? (
        <div>This customer and person is eligible for insurance</div>
      ) : (
        <div>No insurance for this customer and person</div>
      )}
    </AceCardSection>
  );
}
